import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'
import { useToast } from '@/hooks/use-toast'

import { EdunodesExistingAccountNotice } from '@/components/auth/edunodes-existing-account-notice'
import { Button } from '@/components/custom/button'
import { PasswordInput } from '@/components/custom/password-input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'

import type { EdunodesCommunitySummary } from '@/constant/edunodes'
import authServices from '@/services/auth.service'
import { ErrorResponse } from '@/utils/axios-error-handle'

type SignUpFormProps = React.HTMLAttributes<HTMLDivElement> & {
  buttonClassName?: string
}

/** Max length for first/last name on signup (input truncates on paste). */
export const SIGN_UP_NAME_MAX_LENGTH = 50

const formSchema = z
  .object({
    firstName: z
      .string()
      .min(1, { message: 'Please enter your first name' })
      .max(SIGN_UP_NAME_MAX_LENGTH, {
        message: `First name must be at most ${SIGN_UP_NAME_MAX_LENGTH} characters`,
      })
      .refine(val => /[a-zA-Z0-9]/.test(val), {
        message: 'First name must contain at least one non-space, non-special character',
      }),
    lastName: z
      .string()
      .min(1, { message: 'Please enter your last name' })
      .max(SIGN_UP_NAME_MAX_LENGTH, {
        message: `Last name must be at most ${SIGN_UP_NAME_MAX_LENGTH} characters`,
      })
      .refine(val => /[a-zA-Z0-9]/.test(val), {
        message: 'Last name must contain at least one non-space, non-special character',
      }),
    email: z.string().min(1, { message: 'Please enter your email' }).email({ message: 'Invalid email address' }),
    password: z
      .string()
      .min(1, {
        message: 'Please enter your password',
      })
      .min(6, {
        message: 'Password must be at least 6 characters long',
      }),
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match.",
    path: ['confirmPassword'],
  })

type EdunodesExistingAccountState = {
  email: string
  communities: EdunodesCommunitySummary[]
}

export function SignUpForm({ className, buttonClassName, ...props }: SignUpFormProps) {
  const [isLoading, setIsLoading] = React.useState(false)
  const [edunodesExistingAccount, setEdunodesExistingAccount] =
    React.useState<EdunodesExistingAccountState | null>(null)
  const { toast } = useToast()
  const router = useRouter()
  const { text } = useLocale()
  const auth = text.AUTH

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  })

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true)
    try {
      const signUpResult = await authServices.register({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email.toLowerCase(),
        password: data.password,
      })

      if (
        signUpResult &&
        'edunodesExistingAccount' in signUpResult &&
        signUpResult.edunodesExistingAccount
      ) {
        setEdunodesExistingAccount({
          email: signUpResult.email,
          communities: signUpResult.communities,
        })
        return
      }

      if (signUpResult && 'id' in signUpResult) {
        localStorage.setItem('pendingVerifyEmail', data.email.toLowerCase())
        toast({
          variant: 'default',
          title: auth.SIGN_UP_SUCCESS,
        })
        form.reset()
        router.push('/auth/verify-account')
      }
    } catch (error) {
      const _error = error as ErrorResponse
      toast({
        variant: 'destructive',
        description: _error.response?.data.message || auth.SOMETHING_WRONG,
        title: auth.SIGN_UP_ERROR,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      {edunodesExistingAccount ? (
        <>
          <EdunodesExistingAccountNotice
            variant="sign-up"
            email={edunodesExistingAccount.email}
            communities={edunodesExistingAccount.communities}
          />
          <Button
            type="button"
            variant="outline"
            className={buttonClassName}
            onClick={() => {
              form.setValue('email', edunodesExistingAccount.email)
              setEdunodesExistingAccount(null)
            }}
          >
            {auth.USE_DIFFERENT_EMAIL}
          </Button>
        </>
      ) : (
        <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>{auth.FIRST_NAME}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="John"
                        maxLength={SIGN_UP_NAME_MAX_LENGTH}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>{auth.LAST_NAME}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Doe"
                        maxLength={SIGN_UP_NAME_MAX_LENGTH}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>{auth.EMAIL}</FormLabel>
                  <FormControl>
                    <Input placeholder="name@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>{auth.PASSWORD}</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder="********" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>{auth.CONFIRM_PASSWORD}</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder="********" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button className={cn('mt-2', buttonClassName)} loading={isLoading}>
              {auth.CREATE_ACCOUNT}
            </Button>

            {/* <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">{auth.OR_CONTINUE_WITH}</span>
              </div>
            </div> */}

            {/* <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="w-full"
                type="button"
                leftSection={<Icons.google className="h-4 w-4" />}
              >
                Google
              </Button>
            </div> */}
          </div>
        </form>
        </Form>
      )}
    </div>
  )
}
